import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { languageRedirectTarget, resolveLocale } from '@shared/locales';
import { detectBrowserLanguage } from './language-detection';

/**
 * Mount this once, near the root of the app (see app.tsx).
 *
 * Prefixed URLs (`/es/…`) already rendered in that language on the server,
 * and main.tsx set i18next to the same locale before hydration, so a visitor
 * who opened a Spanish link stays on it.
 *
 * Unprefixed URLs are the entry that negotiates. The Worker already
 * redirected from `Accept-Language` before this HTML was served; this effect
 * makes the same decision from `navigator.languages` for a response that was
 * cached or served without that header (`vite dev` included). It navigates
 * with `replace` so the back button does not return to a URL that would
 * immediately leave again. `?lang=` wins over both.
 */
export function I18nBootstrap({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const target = languageRedirectTarget(window.location.pathname, window.location.search, detectBrowserLanguage());
    if (target !== null) window.location.replace(`${target}${window.location.hash}`);
  }, []);

  // Keeps <html lang> in sync with the active language for assistive tech.
  useEffect(() => {
    document.documentElement.lang = resolveLocale(i18n.language);
  }, [i18n.language]);

  return <>{children}</>;
}
